(() => {
  const ROOT_SELECTOR = '[data-ms-sourcing-desk]';
  const POSITION_KEY = 'mingsilk_sourcing_desk_position_r8';
  const MOBILE_DOCK_KEY = 'mingsilk_sourcing_desk_mobile_dock_r81';
  const SESSION_LANDING_KEY = 'mingsilk_sourcing_desk_landing_r81';
  const SESSION_AUTO_KEY = 'mingsilk_sourcing_desk_auto_r81';
  const AUTO_OPEN_DELAY = 700;
  const AUTO_OPEN_DURATION = 6000;
  const MOBILE_DRAG_THRESHOLD = 8;
  const MOBILE_SWIPE_CLOSE = 64;
  const MOBILE_LAUNCHER_SIZE = 52;
  const SUSPENDED_CLASSES = ['mingsilk-v10-nav-open', 'ms-fl-filter-lock', 'ms-mega-menu-open'];

  const init = (root) => {
    if (!root || root.dataset.msSourcingReady === 'true') return;
    root.dataset.msSourcingReady = 'true';

    const launcher = root.querySelector('[data-ms-sourcing-launcher]');
    const panel = root.querySelector('[data-ms-sourcing-panel]');
    const minimize = root.querySelector('[data-ms-sourcing-minimize]');
    const dragHandle = root.querySelector('[data-ms-sourcing-drag-handle]');
    const searchCollection = root.querySelector('[data-ms-search-collection]');
    const continueForm = root.querySelector('[data-ms-continue-form]');
    const whatsapp = root.querySelector('[data-ms-sourcing-whatsapp]');
    const emailLinks = [...root.querySelectorAll('[data-ms-sourcing-email-link]')];
    const copyEmail = root.querySelector('[data-ms-sourcing-copy-email]');
    const copyLabel = root.querySelector('[data-ms-sourcing-copy-label]');

    if (!launcher || !panel) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const observers = [];
    const pageType = root.dataset.pageType || 'general';
    const contextKind = root.dataset.contextKind || 'general';
    const contextTitle = root.dataset.contextTitle || 'General silk sourcing enquiry';
    const shareUrl = root.dataset.shareUrl || '';
    const defaultIntent = root.dataset.defaultIntent || 'Fabric sourcing';
    const emailAddress = root.dataset.email || '';
    const isHome = root.dataset.isHome === 'true';
    const isFormPage = pageType === 'contact' || pageType === 'swatches';

    let device = null;
    let isOpen = false;
    let copyResetTimer = 0;
    let autoStartTimer = 0;
    let autoCloseTimer = 0;
    let desktopDragState = null;
    let mobileDragState = null;
    let sheetDragState = null;
    let suppressLauncherClick = false;
    let mobileDock = readMobileDock();
    let resizeFrame = 0;

    const viewportState = () => {
      const width = Math.max(1, window.innerWidth);
      const height = Math.max(1, window.innerHeight);
      const shortSide = Math.min(width, height);
      const landscape = width > height;
      const fine = window.matchMedia('(pointer: fine)').matches;
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      const compactMobile = shortSide < 600;
      return {
        width,
        height,
        shortSide,
        landscape,
        fine,
        coarse,
        compactMobile,
        desktop: !compactMobile && fine && width >= 900,
        tabletLandscape: !compactMobile && coarse && landscape && width >= 900 && height >= 600,
        tabletPortrait: !compactMobile && coarse && !landscape && shortSide >= 600,
        mobileLandscape: compactMobile && landscape && height < 500,
      };
    };

    function readMobileDock() {
      try {
        const parsed = JSON.parse(localStorage.getItem(MOBILE_DOCK_KEY) || 'null');
        const edge = parsed?.edge === 'left' ? 'left' : 'right';
        const yRatio = Number(parsed?.yRatio);
        if (Number.isFinite(yRatio)) return { edge, yRatio: Math.min(1, Math.max(0, yRatio)) };
      } catch (_) {}
      return { edge: 'right', yRatio: .68 };
    }

    const saveMobileDock = () => {
      try {
        localStorage.setItem(MOBILE_DOCK_KEY, JSON.stringify({
          edge: mobileDock.edge,
          yRatio: Math.round(mobileDock.yRatio * 1000) / 1000,
        }));
      } catch (_) {}
    };

    const readDesktopPosition = () => {
      try {
        const parsed = JSON.parse(localStorage.getItem(POSITION_KEY) || 'null');
        return parsed && Number.isFinite(parsed.right) && Number.isFinite(parsed.bottom) ? parsed : null;
      } catch (_) {
        return null;
      }
    };

    const saveDesktopPosition = (position) => {
      try {
        localStorage.setItem(POSITION_KEY, JSON.stringify({
          right: Math.round(position.right),
          bottom: Math.round(position.bottom),
        }));
      } catch (_) {}
    };

    const applyDesktopPosition = (position) => {
      if (!device?.desktop || !position || !Number.isFinite(position.right) || !Number.isFinite(position.bottom)) {
        root.style.removeProperty('--ms-sd-user-right');
        root.style.removeProperty('--ms-sd-user-bottom');
        return;
      }
      root.style.setProperty('--ms-sd-user-right', position.right + 'px');
      root.style.setProperty('--ms-sd-user-bottom', position.bottom + 'px');
    };

    const currentAnchor = () => {
      const rect = root.getBoundingClientRect();
      return {
        right: Math.max(0, window.innerWidth - rect.right),
        bottom: Math.max(0, window.innerHeight - rect.bottom),
      };
    };

    const clampDesktopPosition = (right, bottom, persist) => {
      if (!device?.desktop) return null;
      const target = isOpen && !panel.hidden ? panel : launcher;
      const rect = target.getBoundingClientRect();
      const margin = 24;
      const next = {
        right: Math.min(Math.max(right, margin), Math.max(margin, window.innerWidth - rect.width - margin)),
        bottom: Math.min(Math.max(bottom, margin), Math.max(margin, window.innerHeight - rect.height - margin)),
      };
      applyDesktopPosition(next);
      if (persist) saveDesktopPosition(next);
      return next;
    };

    const clampDesktopCurrent = (persist) => {
      const anchor = currentAnchor();
      return clampDesktopPosition(anchor.right, anchor.bottom, persist);
    };

    const mobileVisualViewport = () => {
      const visual = window.visualViewport;
      return {
        width: visual?.width || window.innerWidth,
        height: visual?.height || window.innerHeight,
        left: visual?.offsetLeft || 0,
        top: visual?.offsetTop || 0,
      };
    };

    const visibleTrayHeight = () => {
      const tray = document.querySelector('[data-ms-shortlist-tray]');
      if (!tray || tray.hidden || !tray.getClientRects().length) return 0;
      return Math.ceil(tray.getBoundingClientRect().height) + 12;
    };

    const mobileDockBounds = () => {
      const viewport = mobileVisualViewport();
      const headerValue = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
      const minTop = Math.max(viewport.top + 12, viewport.top + Math.min(headerValue + 8, viewport.height * .28));
      const maxTop = Math.max(minTop, viewport.top + viewport.height - MOBILE_LAUNCHER_SIZE - 18 - visibleTrayHeight());
      return { viewport, minTop, maxTop };
    };

    const applyMobileDock = (persist) => {
      if (!device?.compactMobile) return;
      const { viewport, minTop, maxTop } = mobileDockBounds();
      const range = Math.max(0, maxTop - minTop);
      const top = minTop + range * mobileDock.yRatio;
      const left = mobileDock.edge === 'left'
        ? Math.max(0, viewport.left)
        : Math.max(0, viewport.left + viewport.width - MOBILE_LAUNCHER_SIZE);

      root.classList.toggle('is-docked-left', mobileDock.edge === 'left');
      root.classList.toggle('is-docked-right', mobileDock.edge !== 'left');
      root.style.setProperty('--ms-sd-mobile-left', Math.round(left) + 'px');
      root.style.setProperty('--ms-sd-mobile-top', Math.round(top) + 'px');
      launcher.setAttribute(
        'aria-label',
        'Open MingSilk sourcing desk; docked on the ' + mobileDock.edge + ' side. Drag vertically or to the other side.'
      );
      if (persist) saveMobileDock();
    };

    const syncVisualHeight = () => {
      if (!device?.compactMobile) {
        root.style.removeProperty('--ms-sd-visual-height');
        return;
      }
      const height = window.visualViewport?.height || window.innerHeight;
      root.style.setProperty('--ms-sd-visual-height', Math.max(1, Math.round(height)) + 'px');
    };

    const syncDeviceMode = () => {
      device = viewportState();
      root.classList.toggle('is-compact-mobile', device.compactMobile);
      root.classList.toggle('is-mobile-landscape', device.mobileLandscape);
      root.classList.toggle('is-tablet-landscape', device.tabletLandscape);
      root.classList.toggle('is-tablet-portrait', device.tabletPortrait);
      root.classList.toggle('is-desktop-draggable', device.desktop);

      if (device.compactMobile) {
        applyDesktopPosition(null);
        syncVisualHeight();
        applyMobileDock(false);
      } else {
        root.classList.remove('is-docked-left', 'is-docked-right', 'is-mobile-dragging', 'is-sheet-dragging');
        root.style.removeProperty('--ms-sd-mobile-left');
        root.style.removeProperty('--ms-sd-mobile-top');
        root.style.removeProperty('--ms-sd-visual-height');
        launcher.setAttribute('aria-label', 'Open MingSilk sourcing desk');
        if (device.desktop) applyDesktopPosition(readDesktopPosition());
        else applyDesktopPosition(null);
        requestAnimationFrame(() => clampDesktopCurrent(false));
      }
    };

    const track = (eventName, route, metadata) => {
      const payload = {
        event: eventName,
        page_type: pageType,
        route: route || null,
        context_kind: contextKind,
      };
      if (metadata?.trigger) payload.trigger = metadata.trigger;
      window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    };

    const clearAutoTimers = () => {
      window.clearTimeout(autoStartTimer);
      window.clearTimeout(autoCloseTimer);
      autoStartTimer = 0;
      autoCloseTimer = 0;
    };

    const keepAutoPanelOpen = () => {
      if (!autoCloseTimer) return;
      window.clearTimeout(autoCloseTimer);
      autoCloseTimer = 0;
    };

    const resetSheetPull = () => {
      root.classList.remove('is-sheet-dragging');
      panel.style.removeProperty('transform');
      sheetDragState = null;
    };

    const setOpen = (next, options) => {
      const config = options || {};
      const nextOpen = Boolean(next);
      if (nextOpen === isOpen) return;

      isOpen = nextOpen;
      root.classList.toggle('is-open', isOpen);
      root.classList.toggle('is-mobile-sheet-open', isOpen && Boolean(device?.compactMobile));
      launcher.setAttribute('aria-expanded', String(isOpen));
      panel.hidden = !isOpen;

      if (isOpen) {
        track('sourcing_desk_open', null, { trigger: config.trigger || 'launcher' });
        requestAnimationFrame(() => {
          if (device?.desktop) clampDesktopCurrent(false);
          if (config.focus !== false) panel.focus({ preventScroll: true });
        });
      } else {
        resetSheetPull();
        if (config.userInitiated) track('sourcing_desk_close');
        if (device?.compactMobile) applyMobileDock(false);
        if (config.returnFocus) launcher.focus({ preventScroll: true });
      }
    };

    launcher.addEventListener('click', (event) => {
      if (suppressLauncherClick) {
        event.preventDefault();
        suppressLauncherClick = false;
        return;
      }
      clearAutoTimers();
      setOpen(true, { trigger: 'launcher' });
    }, { signal });

    minimize?.addEventListener('click', () => {
      keepAutoPanelOpen();
      setOpen(false, { userInitiated: true, returnFocus: true });
    }, { signal });

    document.addEventListener('mingsilk:sourcing-desk-open', (event) => {
      clearAutoTimers();
      const trigger = event.detail?.trigger === 'continue' ? 'continue' : 'launcher';
      setOpen(true, { trigger });
    }, { signal });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen) return;
      event.preventDefault();
      keepAutoPanelOpen();
      setOpen(false, { userInitiated: true, returnFocus: true });
    }, { signal });

    document.addEventListener('pointerdown', (event) => {
      if (!isOpen || root.contains(event.target)) return;
      keepAutoPanelOpen();
      setOpen(false, { userInitiated: true, returnFocus: false });
    }, { capture: true, signal });

    const beginDesktopDrag = (event, launcherHandle) => {
      if (!device?.desktop || event.button !== 0) return;
      if (!launcherHandle && event.target.closest('button, a, input, select, textarea')) return;

      clearAutoTimers();
      const anchor = currentAnchor();
      desktopDragState = {
        launcherHandle,
        startX: event.clientX,
        startY: event.clientY,
        right: anchor.right,
        bottom: anchor.bottom,
        moved: false,
      };
    };

    launcher.addEventListener('mousedown', (event) => beginDesktopDrag(event, true), { signal });
    dragHandle?.addEventListener('mousedown', (event) => beginDesktopDrag(event, false), { signal });

    document.addEventListener('mousemove', (event) => {
      if (!desktopDragState || !device?.desktop) return;
      const dx = event.clientX - desktopDragState.startX;
      const dy = event.clientY - desktopDragState.startY;
      if (!desktopDragState.moved && Math.hypot(dx, dy) < 5) return;

      desktopDragState.moved = true;
      root.classList.add('is-desktop-dragging');
      clampDesktopPosition(desktopDragState.right - dx, desktopDragState.bottom - dy, false);
      event.preventDefault();
    }, { signal });

    const finishDesktopDrag = () => {
      if (!desktopDragState) return;
      const moved = desktopDragState.moved;
      const launcherHandle = desktopDragState.launcherHandle;
      if (moved) {
        clampDesktopCurrent(true);
        if (launcherHandle) {
          suppressLauncherClick = true;
          window.setTimeout(() => { suppressLauncherClick = false; }, 400);
        }
      }
      root.classList.remove('is-desktop-dragging');
      desktopDragState = null;
    };

    document.addEventListener('mouseup', finishDesktopDrag, { signal });
    window.addEventListener('blur', finishDesktopDrag, { signal });

    launcher.addEventListener('pointerdown', (event) => {
      if (!device?.compactMobile || event.button !== 0) return;
      clearAutoTimers();
      const rect = launcher.getBoundingClientRect();
      mobileDragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        offsetY: event.clientY - rect.top,
        moved: false,
      };
      try { launcher.setPointerCapture(event.pointerId); } catch (_) {}
    }, { signal });

    launcher.addEventListener('pointermove', (event) => {
      if (!mobileDragState || event.pointerId !== mobileDragState.pointerId || !device?.compactMobile) return;
      const dx = event.clientX - mobileDragState.startX;
      const dy = event.clientY - mobileDragState.startY;
      if (!mobileDragState.moved && Math.hypot(dx, dy) < MOBILE_DRAG_THRESHOLD) return;

      mobileDragState.moved = true;
      root.classList.add('is-mobile-dragging');
      mobileDock.edge = event.clientX < window.innerWidth / 2 ? 'left' : 'right';

      const { minTop, maxTop } = mobileDockBounds();
      const top = Math.min(Math.max(event.clientY - mobileDragState.offsetY, minTop), maxTop);
      const range = Math.max(1, maxTop - minTop);
      mobileDock.yRatio = Math.min(1, Math.max(0, (top - minTop) / range));
      applyMobileDock(false);
      event.preventDefault();
    }, { signal });

    const finishMobileDrag = (event) => {
      if (!mobileDragState || (event && event.pointerId !== mobileDragState.pointerId)) return;
      const moved = mobileDragState.moved;
      if (moved) {
        applyMobileDock(true);
        suppressLauncherClick = true;
        window.setTimeout(() => { suppressLauncherClick = false; }, 400);
      }
      root.classList.remove('is-mobile-dragging');
      mobileDragState = null;
    };

    launcher.addEventListener('pointerup', finishMobileDrag, { signal });
    launcher.addEventListener('pointercancel', finishMobileDrag, { signal });

    dragHandle?.addEventListener('pointerdown', (event) => {
      if (!device?.compactMobile || event.button !== 0) return;
      if (event.target.closest('button, a, input, select, textarea')) return;
      keepAutoPanelOpen();
      sheetDragState = {
        pointerId: event.pointerId,
        startY: event.clientY,
        distance: 0,
      };
      try { dragHandle.setPointerCapture(event.pointerId); } catch (_) {}
    }, { signal });

    dragHandle?.addEventListener('pointermove', (event) => {
      if (!sheetDragState || event.pointerId !== sheetDragState.pointerId || !device?.compactMobile) return;
      const distance = Math.max(0, event.clientY - sheetDragState.startY);
      sheetDragState.distance = distance;
      if (distance < MOBILE_DRAG_THRESHOLD) return;
      root.classList.add('is-sheet-dragging');
      panel.style.transform = 'translateY(' + Math.round(distance) + 'px)';
      event.preventDefault();
    }, { signal });

    const finishSheetDrag = (event) => {
      if (!sheetDragState || (event && event.pointerId !== sheetDragState.pointerId)) return;
      const shouldClose = sheetDragState.distance >= MOBILE_SWIPE_CLOSE;
      resetSheetPull();
      if (shouldClose) setOpen(false, { userInitiated: true, returnFocus: false });
    };

    dragHandle?.addEventListener('pointerup', finishSheetDrag, { signal });
    dragHandle?.addEventListener('pointercancel', finishSheetDrag, { signal });

    panel.addEventListener('pointerdown', keepAutoPanelOpen, { capture: true, signal });
    panel.addEventListener('keydown', keepAutoPanelOpen, { capture: true, signal });

    root.querySelectorAll('[data-ms-sourcing-route]').forEach((link) => {
      link.addEventListener('click', () => {
        track('sourcing_desk_route_click', link.dataset.msSourcingRoute || 'unknown');
      }, { signal });
    });

    if (searchCollection) {
      searchCollection.addEventListener('click', (event) => {
        const library = document.querySelector('[data-ms-fabric-library]');
        const searchInput = library?.querySelector('[data-ms-library-search]');
        if (!library || !searchInput) return;

        event.preventDefault();
        setOpen(false, { userInitiated: false, returnFocus: false });
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        searchInput.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        searchInput.focus({ preventScroll: true });
      }, { signal });
    }

    const findTarget = (targetList) => {
      const selectors = String(targetList || '').split('|').filter(Boolean);
      for (const selector of selectors) {
        try {
          const target = document.querySelector(selector);
          if (target) return target;
        } catch (_) {}
      }
      return null;
    };

    if (continueForm) {
      continueForm.addEventListener('click', (event) => {
        const target = findTarget(continueForm.dataset.msFormTarget);
        if (!target) return;
        event.preventDefault();
        setOpen(false, { userInitiated: false, returnFocus: false });
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        window.setTimeout(() => target.focus({ preventScroll: true }), reducedMotion ? 0 : 260);
      }, { signal });
    }

    const buildMessage = (channel) => {
      const lines = [
        'Hello MingSilk, I’d like help with ' + defaultIntent + '.',
        '',
        'Fabric or page: ' + contextTitle,
        'Intended use:',
        'Approx. quantity:',
        'Delivery country:',
        'Question:',
      ];
      if (shareUrl) lines.push('', 'Page: ' + shareUrl);
      if (channel === 'email') lines.splice(5, 0, 'Target timing:');
      return lines.join('\n');
    };

    if (emailAddress) {
      const subject = 'MingSilk enquiry — ' + defaultIntent;
      const emailHref = 'mailto:' + emailAddress + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(buildMessage('email'));
      emailLinks.forEach((link) => {
        link.href = emailHref;
        link.addEventListener('click', () => track('sourcing_desk_email_click'), { signal });
      });
    } else {
      emailLinks.forEach((link) => { link.hidden = true; });
      if (copyEmail) copyEmail.hidden = true;
    }

    if (whatsapp) {
      const phone = String(root.dataset.whatsapp || '').replace(/[^0-9]/g, '');
      if (!phone) {
        whatsapp.hidden = true;
      } else {
        whatsapp.href = 'https://wa.me/' + phone + '?text=' + encodeURIComponent(buildMessage('whatsapp'));
        whatsapp.addEventListener('click', () => track('sourcing_desk_whatsapp_click'), { signal });
      }
    }

    const fallbackCopy = (value) => {
      const helper = document.createElement('textarea');
      helper.value = value;
      helper.setAttribute('readonly', '');
      helper.style.position = 'fixed';
      helper.style.opacity = '0';
      helper.style.pointerEvents = 'none';
      document.body.append(helper);
      helper.select();
      let copied = false;
      try { copied = document.execCommand('copy'); } catch (_) {}
      helper.remove();
      return copied;
    };

    copyEmail?.addEventListener('click', async () => {
      let copied = false;
      try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(emailAddress);
          copied = true;
        }
      } catch (_) {}
      if (!copied) copied = fallbackCopy(emailAddress);

      track('sourcing_desk_email_copy');
      if (copyLabel) {
        window.clearTimeout(copyResetTimer);
        copyLabel.textContent = copied ? 'Copied ✓' : emailAddress;
        copyResetTimer = window.setTimeout(() => { copyLabel.textContent = 'Copy'; }, 1800);
      }
    }, { signal });

    const cleanSourcingParams = () => {
      const url = new URL(window.location.href);
      let changed = false;
      [...url.searchParams.keys()].forEach((key) => {
        if (key.indexOf('ms_') === 0) {
          url.searchParams.delete(key);
          changed = true;
        }
      });
      if (!changed) return;
      const search = url.searchParams.toString();
      history.replaceState(history.state, '', url.pathname + (search ? '?' + search : '') + url.hash);
    };

    const setHidden = (form, name, value) => {
      if (!value) return;
      let field = form.querySelector('input[name="' + name + '"]');
      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = name;
        form.append(field);
      }
      field.value = value;
    };

    const destinationForm = () => {
      if (pageType === 'contact') return document.querySelector('#ReverieContactForm');
      if (pageType !== 'swatches') return null;
      const target = findTarget('[data-ms-swatches-form]|#inquiry|form[action*="/contact"]');
      if (!target) return null;
      if (target.matches('form')) return target;
      return target.querySelector('form') || document.querySelector('form[action*="/contact"]');
    };

    const hydrateDestinationContext = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('ms_sd') !== '1') return true;

      const form = destinationForm();
      if (!form) return false;

      const context = params.get('ms_context') || '';
      const sourceUrl = params.get('ms_url') || '';
      const intent = params.get('ms_intent') || 'Fabric sourcing';

      if (pageType === 'contact') {
        const radios = [...form.querySelectorAll('input[name="contact[inquiry_type]"]')];
        const radio = radios.find((item) => item.value === intent);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const interested = form.querySelector('input[name="contact[interested_fabrics]"]');
        if (interested && !interested.value && context) interested.value = context;

        const message = form.querySelector('textarea[name="contact[body]"]');
        if (message && !message.value && (context || sourceUrl)) {
          message.value = [
            context ? 'Page context: ' + context : '',
            sourceUrl ? 'Source page: ' + sourceUrl : '',
          ].filter(Boolean).join('\n');
        }
      }

      setHidden(form, 'contact[sourcing_desk_context]', context);
      setHidden(form, 'contact[sourcing_desk_url]', sourceUrl);
      setHidden(form, 'contact[sourcing_desk_intent]', intent);
      cleanSourcingParams();
      return true;
    };

    if (!hydrateDestinationContext()) {
      window.setTimeout(hydrateDestinationContext, 450);
    }

    const syncSuspended = () => {
      const blocked = SUSPENDED_CLASSES.some((className) => document.body.classList.contains(className));
      root.classList.toggle('is-suspended', blocked);
      if (blocked && isOpen) setOpen(false, { userInitiated: false, returnFocus: false });
    };

    syncSuspended();
    if (typeof MutationObserver === 'function') {
      const bodyObserver = new MutationObserver(syncSuspended);
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      observers.push(bodyObserver);
    }

    const tray = document.querySelector('[data-ms-shortlist-tray]');
    const syncBottomOffset = () => {
      if (!tray || tray.hidden || !tray.getClientRects().length) {
        root.style.setProperty('--ms-sd-bottom-offset', '0px');
      } else {
        const height = Math.ceil(tray.getBoundingClientRect().height);
        root.style.setProperty('--ms-sd-bottom-offset', height + 12 + 'px');
      }
      if (device?.compactMobile) applyMobileDock(false);
    };

    syncBottomOffset();
    if (tray && typeof MutationObserver === 'function') {
      const trayObserver = new MutationObserver(syncBottomOffset);
      trayObserver.observe(tray, {
        attributes: true,
        attributeFilter: ['hidden', 'class', 'style'],
        childList: true,
        subtree: true,
      });
      observers.push(trayObserver);
    }

    syncDeviceMode();

    const sessionState = (() => {
      try {
        let landing = sessionStorage.getItem(SESSION_LANDING_KEY);
        if (!landing) {
          landing = isHome ? 'home' : 'other';
          sessionStorage.setItem(SESSION_LANDING_KEY, landing);
        }
        return {
          available: true,
          landing,
          autoUsed: sessionStorage.getItem(SESSION_AUTO_KEY) === '1',
        };
      } catch (_) {
        return { available: false, landing: 'other', autoUsed: true };
      }
    })();

    const autoDeviceEligible = () => Boolean(device?.desktop || device?.tabletLandscape);
    const autoEligible = sessionState.available
      && sessionState.landing === 'home'
      && !sessionState.autoUsed
      && isHome
      && !isFormPage
      && autoDeviceEligible();

    if (autoEligible) {
      try { sessionStorage.setItem(SESSION_AUTO_KEY, '1'); } catch (_) {}
      autoStartTimer = window.setTimeout(() => {
        autoStartTimer = 0;
        if (!autoDeviceEligible() || !isHome || isFormPage || isOpen || document.visibilityState !== 'visible') return;
        setOpen(true, { trigger: 'auto', focus: false });
        autoCloseTimer = window.setTimeout(() => {
          autoCloseTimer = 0;
          if (isOpen) setOpen(false, { userInitiated: false, returnFocus: false });
        }, AUTO_OPEN_DURATION);
      }, AUTO_OPEN_DELAY);
    }

    const scheduleViewportSync = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        syncDeviceMode();
        syncBottomOffset();
        if (device?.desktop) clampDesktopCurrent(false);
      });
    };

    window.addEventListener('resize', scheduleViewportSync, { passive: true, signal });
    window.addEventListener('orientationchange', scheduleViewportSync, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', scheduleViewportSync, { passive: true, signal });
    window.visualViewport?.addEventListener('scroll', scheduleViewportSync, { passive: true, signal });
    window.addEventListener('reverie:inquiry-basket-change', syncBottomOffset, { signal });

    root._msSourcingCleanup = () => {
      controller.abort();
      observers.forEach((observer) => observer.disconnect());
      window.cancelAnimationFrame(resizeFrame);
      window.clearTimeout(copyResetTimer);
      clearAutoTimers();
      root.dataset.msSourcingReady = 'false';
    };
  };

  const initAll = (context) => (context || document).querySelectorAll(ROOT_SELECTOR).forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => root._msSourcingCleanup?.());
  });
})();
